import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import BASE_URL from '../../url';
import '../styles/JobReport.css';
import ManagerDashboard from './ManagerDashboard';
import SubmissionReport from './SubmissionReport';
import ClientCountReport from './ClientCountReport';
import CreatePodModal from './CreatePodModal';
import AddMemberModal from './AddMemberModal';
import EditPodModal from './EditPodModal';
import DeletePodModal from './DeletePodModal';
import { Edit2, Trash2, Users, UsersRound, FileCheck2, Building2, Search, ChevronDown, X, Download } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { usePersistentFilters } from '../../hooks/usePersistentFilters';
import { generateSubmissionReportHtml, generateExecutiveDeckHtml } from '../utils/reportExportUtils';

// ─── Searchable Client Dropdown Component ─────────────────────────────────────
const SearchableClientSelect = ({ clients, selectedClient, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredClients = (clients || []).filter(c =>
    (c.clientName || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="input-group" ref={containerRef} style={{ position: "relative" }}>
      <label style={{ fontSize: "12px", color: "#64748b", fontWeight: "600", marginBottom: "4px", display: "block" }}>Client</label>
      <div
        onClick={() => setIsOpen(prev => !prev)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "7px 12px", background: "white", border: "1px solid #cbd5e1",
          borderRadius: "6px", cursor: "pointer", fontSize: "13px", color: "#1e293b",
          minWidth: "180px", userSelect: "none", height: "36px", boxSizing: "border-box"
        }}
      >
        <span style={{ fontWeight: selectedClient ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selectedClient ? selectedClient.name : "All Clients"}
        </span>
        <ChevronDown size={14} color="#64748b" style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", marginLeft: "8px", flexShrink: 0 }} />
      </div>

      {isOpen && (
        <div
          style={{
            position: "absolute", top: "100%", left: 0, marginTop: "4px",
            width: "220px", background: "white", border: "1px solid #cbd5e1",
            borderRadius: "8px", boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
            zIndex: 99999, overflow: "hidden"
          }}
        >
          {/* Search Input Box */}
          <div style={{ padding: "8px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: "6px", background: "#f8fafc" }}>
            <Search size={14} color="#94a3b8" />
            <input
              type="text"
              autoFocus
              placeholder="Search client…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%", border: "none", outline: "none", background: "transparent",
                fontSize: "12px", color: "#1e293b"
              }}
            />
            {search && (
              <X size={12} color="#94a3b8" style={{ cursor: "pointer" }} onClick={() => setSearch("")} />
            )}
          </div>

          {/* Options List */}
          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            <div
              onClick={() => { onSelect(null); setIsOpen(false); setSearch(""); }}
              style={{
                padding: "8px 12px", fontSize: "12px", cursor: "pointer",
                background: !selectedClient ? "#f0fdf4" : "transparent",
                color: !selectedClient ? "#166534" : "#334155",
                fontWeight: !selectedClient ? 600 : 400
              }}
            >
              All Clients
            </div>
            {filteredClients.length === 0 ? (
              <div style={{ padding: "10px", fontSize: "12px", color: "#94a3b8", textAlign: "center" }}>No clients found</div>
            ) : (
              filteredClients.map(c => {
                const isSelected = selectedClient?.name === c.clientName;
                return (
                  <div
                    key={c.clientName}
                    onClick={() => { onSelect(c.clientName); setIsOpen(false); setSearch(""); }}
                    style={{
                      padding: "8px 12px", fontSize: "12px", cursor: "pointer",
                      background: isSelected ? "#e0f2fe" : "transparent",
                      color: isSelected ? "#0369a1" : "#334155",
                      fontWeight: isSelected ? 600 : 400
                    }}
                  >
                    {c.clientName}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const toYMD = (d) => {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const JobReport = () => {
  const [reportData, setReportData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [draggedMember, setDraggedMember] = useState(null);
  const [dragOverLeadId, setDragOverLeadId] = useState(null);
  const [moveMessage, setMoveMessage] = useState('');
  const [highlightedPod, setHighlightedPod] = useState(null);

  // Persistent filters — survive a full page reload
  const today = toYMD(new Date());
  const initialJobFilters = { startDate: today, endDate: today, regionFilter: 'Onsite', dropdownSelection: 'today', selectedClient: null, activeFilter: null, chartToggle: 'Daily' };
  const { filters: jobFilters, setFilters: setJobFilters, handleClearFilter: handleClearJobFilters, handleRefresh: handlePageRefresh } = usePersistentFilters('jobReportFilters_v3', initialJobFilters);
  const { startDate, endDate, regionFilter, dropdownSelection, selectedClient, activeFilter = null, chartToggle = 'Daily' } = jobFilters;

  const setStartDate = (val) => setJobFilters(prev => ({ ...prev, startDate: val }));
  const setEndDate = (val) => setJobFilters(prev => ({ ...prev, endDate: val }));
  const setRegionFilter = (val) => setJobFilters(prev => ({ ...prev, regionFilter: val }));
  const setDropdownSelection = (val) => setJobFilters(prev => ({ ...prev, dropdownSelection: val }));
  const setSelectedClient = (val) => setJobFilters(prev => ({ ...prev, selectedClient: typeof val === 'function' ? val(prev.selectedClient) : val }));
  const setActiveFilter = (val) => setJobFilters(prev => ({ ...prev, activeFilter: val }));
  const setChartToggle = (val) => setJobFilters(prev => ({ ...prev, chartToggle: val }));
  const [jobStats, setJobStats] = useState({ totalJobs: 0, jobsWithSubs: 0, jobsWithoutSubs: 0 });
  const [clientOptions, setClientOptions] = useState([]);

  // Fetch client list for dropdown (filtered by selected region)
  useEffect(() => {
    const fetchClientOptions = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${BASE_URL}/api/jobs/stats/client-counts`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { startDate, endDate, region: regionFilter }
        });
        if (res.data.success) {
          setClientOptions(res.data.clients || []);
        }
      } catch (err) {
        console.error("Error fetching client options:", err);
      }
    };
    fetchClientOptions();
  }, [startDate, endDate, regionFilter]);

  // Modal states
  const [isCreatePodOpen, setIsCreatePodOpen] = useState(false);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [isEditPodOpen, setIsEditPodOpen] = useState(false);
  const [isDeletePodOpen, setIsDeletePodOpen] = useState(false);
  const [targetTeamIdForMember, setTargetTeamIdForMember] = useState(null);
  const [targetPodData, setTargetPodData] = useState(null); // { teamId, teamName, regionId }
  const [isReportDropdownOpen, setIsReportDropdownOpen] = useState(false);
  const reportDropdownRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (reportDropdownRef.current && !reportDropdownRef.current.contains(e.target)) {
        setIsReportDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const handleGenerateReport = async (reportType) => {
    setIsReportDropdownOpen(false);

    if (reportType === 'standard') {
      generateSubmissionReportHtml({
        startDate,
        endDate,
        regionFilter,
        selectedClient: selectedClient ? selectedClient.name : 'All Clients',
        activeFilter,
        jobStats,
        reportData,
        totalSubmissions,
        topRecruiter: topRecruiterObj,
        topPod: topPodObj
      });
      return;
    }

    const today = new Date();
    let repStart = startDate;
    let repEnd = endDate;

    if (reportType === 'today') {
      repStart = toYMD(today);
      repEnd = toYMD(today);
    } else if (reportType === 'weekly') {
      if (activeFilter === 'weekly' && startDate && endDate) {
        repStart = startDate;
        repEnd = endDate;
      } else {
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1);
        const startOfWeek = new Date(today.getFullYear(), today.getMonth(), diff);
        const endOfWeek = new Date(today.getFullYear(), today.getMonth(), diff + 4); // Mon - Fri
        repStart = toYMD(startOfWeek);
        repEnd = toYMD(endOfWeek);
      }
    } else if (reportType === 'monthly') {
      if (activeFilter === 'monthly' && startDate && endDate) {
        repStart = startDate;
        repEnd = endDate;
      } else {
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        repStart = toYMD(startOfMonth);
        repEnd = toYMD(today);
      }
    }

    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/recruitment/applications/job-report/executive-data`, {
        params: {
          startDate: repStart,
          endDate: repEnd,
          region: regionFilter,
          client: selectedClient ? selectedClient.name : 'all'
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      const execData = res.data || {};
      generateExecutiveDeckHtml({
        startDate: repStart,
        endDate: repEnd,
        regionFilter,
        selectedClient: selectedClient ? selectedClient.name : 'All Clients',
        summary: execData.summary || {
          totalOpenJobs: jobStats.totalJobs || 0,
          totalSubmissions: totalSubmissions || 0,
          totalInterviews: 0,
          totalClosures: 0
        },
        podData: (execData.podData && execData.podData.length > 0) ? execData.podData : reportData,
        keyClosures: execData.keyClosures,
        interviewsAwaiting: execData.interviewsAwaiting,
        onboardingPipeline: execData.onboardingPipeline
      });
    } catch (err) {
      console.warn("Could not fetch executive data from server, using live data:", err);
      generateExecutiveDeckHtml({
        startDate: repStart,
        endDate: repEnd,
        regionFilter,
        selectedClient: selectedClient ? selectedClient.name : 'All Clients',
        summary: {
          totalOpenJobs: jobStats.totalJobs || 0,
          totalSubmissions: totalSubmissions || 0,
          totalInterviews: 0,
          totalClosures: 0
        },
        podData: reportData
      });
    }
  };

  // Set default dates on mount and auto-fetch
  useEffect(() => {
    // If no saved filters, startDate/endDate will already be today (from initialJobFilters)
    fetchReport(startDate, endDate, regionFilter, selectedClient); // Auto-fetch on mount using persisted or default dates
    // eslint-disable-next-line
  }, []);

  const fetchReport = async (sDate = startDate, eDate = endDate, reg = regionFilter, cli = selectedClient) => {
    const region = reg;
    const client = cli ? cli.name : undefined;

    if (!sDate || !eDate) {
      setError('Please select both start and end dates.');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/recruitment/applications/job-report`, {
        params: { startDate: sDate, endDate: eDate, region, client },
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.podData) {
        setReportData(response.data.podData);
        setTrendData(response.data.dailyTrends || []);
        setJobStats(response.data.jobStats || { totalJobs: 0, jobsWithSubs: 0, jobsWithoutSubs: 0 });
      } else {
        setReportData(response.data);
      }
    } catch (err) {
      console.error('Error fetching job report:', err);
      setError('Failed to fetch job report data.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDashboardFilter = (filterType) => {
    const today = new Date();
    let start = new Date(today);

    if (filterType === 'daily') {
      start.setDate(today.getDate() - 30); // Last 30 days for Daily View
    } else if (filterType === 'weekly') {
      start.setMonth(today.getMonth() - 1); // Last 1 month for Weekly View
    } else if (filterType === 'monthly') {
      start.setFullYear(today.getFullYear() - 1); // Last 1 year for Monthly View
    } else if (filterType === 'yearly') {
      start.setFullYear(today.getFullYear() - 5); // Last 5 years for Yearly View
    }

    const newStart = toYMD(start);
    const newEnd = toYMD(today);

    setJobFilters(prev => ({
      ...prev,
      startDate: newStart,
      endDate: newEnd,
      activeFilter: filterType,
      chartToggle: filterType.charAt(0).toUpperCase() + filterType.slice(1),
      dropdownSelection: 'custom'
    }));
    // Pass dates directly so fetchReport doesn't use stale state
    fetchReport(newStart, newEnd);
  };

  const handleDropdownChange = (e) => {
    const val = e.target.value;
    setDropdownSelection(val);

    if (val === 'custom') return;

    const today = new Date();
    let start = new Date(today);
    let end = new Date(today);

    if (val === 'today') {
      start = new Date(today);
      end = new Date(today);
    } else if (val === 'yesterday') {
      start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
      end = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    } else if (val === 'this_week') {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Monday
      start = new Date(today.getFullYear(), today.getMonth(), diff);
      end = new Date(today);
    } else if (val === 'last_week') {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1) - 7;
      start = new Date(today.getFullYear(), today.getMonth(), diff);
      end = new Date(today.getFullYear(), today.getMonth(), diff + 6);
    } else if (val === 'this_month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
    } else if (val === 'last_month') {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (val === 'this_quarter') {
      const quarter = Math.floor(today.getMonth() / 3);
      start = new Date(today.getFullYear(), quarter * 3, 1);
      end = new Date(today);
    } else if (val === 'last_quarter') {
      const quarter = Math.floor(today.getMonth() / 3) - 1;
      start = new Date(today.getFullYear(), quarter * 3, 1);
      end = new Date(today.getFullYear(), (quarter + 1) * 3, 0);
    }

    const newStart = toYMD(start);
    const newEnd = toYMD(end);

    setJobFilters(prev => ({
      ...prev,
      startDate: newStart,
      endDate: newEnd,
      dropdownSelection: val,
      activeFilter: null
    }));
    fetchReport(newStart, newEnd);
  };

  // Helper to extract a solid hex color from a linear-gradient string for SVG charts
  const extractSolidColor = (colorStr) => {
    if (!colorStr) return '#0ea5e9';
    if (colorStr.includes('linear-gradient')) {
      const match = colorStr.match(/#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})/);
      return match ? match[0] : '#0ea5e9';
    }
    return colorStr;
  };

  // Color palette for team cards as per design
  const gradientColorsList = [
    'linear-gradient(135deg, #0ea5e9, #2563eb)', // Teal to Blue
    'linear-gradient(135deg, #ef4444, #dc2626)', // Red
    'linear-gradient(135deg, #8b5cf6, #6366f1)', // Purple
    'linear-gradient(135deg, #f97316, #ea580c)', // Orange
    'linear-gradient(135deg, #84cc16, #65a30d)', // Green
    'linear-gradient(135deg, #ec4899, #db2777)'  // Pink
  ];

  const cardColors = gradientColorsList.map(bg => ({ bg }));

  // Helper to map a solid color from DB back to its full gradient
  const getGradientFromSolid = (solidHex) => {
    if (!solidHex) return null;
    if (solidHex.includes('linear-gradient')) return solidHex;
    const match = gradientColorsList.find(g => g.toLowerCase().includes(solidHex.toLowerCase()));
    return match || solidHex;
  };

  const totalTeams = reportData.length;
  const totalMembers = reportData.reduce((acc, team) => acc + (team.members ? team.members.length : 0), 0);
  const totalSubmissions = reportData.reduce((acc, team) => acc + team.totalSubmissions, 0);

  let topRecruiterObj = { name: '-', count: 0 };
  (reportData || []).forEach(t => {
    (t.members || []).forEach(m => {
      if ((m.submissionCount || 0) > topRecruiterObj.count) {
        topRecruiterObj = { name: m.name, count: m.submissionCount };
      }
    });
  });

  let topPodObj = { name: '-', count: 0 };
  (reportData || []).forEach(t => {
    if ((t.totalSubmissions || 0) > topPodObj.count) {
      topPodObj = { name: t.teamName, count: t.totalSubmissions };
    }
  });

  // ---- Drag and Drop Handlers ----
  const handleDragStart = (e, member, fromLeadId) => {
    setDraggedMember({ memberId: member.id, memberName: member.name, fromLeadId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, targetLeadId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverLeadId(targetLeadId);
  };

  const handleDragLeave = (e) => {
    // Only clear the drop target if the mouse genuinely left the card
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverLeadId(null);
    }
  };

  const handleDrop = async (e, targetLeadId) => {
    e.preventDefault();
    setDragOverLeadId(null);
    if (!draggedMember) return;
    if (draggedMember.fromLeadId === targetLeadId) return; // same pod, no action

    // Show confirmation before moving
    if (!window.confirm(`Are you sure you want to move ${draggedMember.memberName} to this team?`)) {
      setDraggedMember(null);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      try {
        await axios.put(`${BASE_URL}/api/teams/move-member`, {
          memberId: draggedMember.memberId,
          newLeadUserId: targetLeadId
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (moveErr) {
        if (moveErr.response && moveErr.response.status === 404) {
          // Fallback to recruitment prefix
          await axios.put(`${BASE_URL}/api/recruitment/teams/move-member`, {
            memberId: draggedMember.memberId,
            newLeadUserId: targetLeadId
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          throw moveErr;
        }
      }

      // Optimistic UI update: move member locally without a full refetch
      setReportData(prev => {
        const updated = prev.map(team => ({ ...team, members: [...(team.members || [])] }));
        const fromTeam = updated.find(t => (t.leadId || -t.teamId) === draggedMember.fromLeadId);
        const toTeam = updated.find(t => (t.leadId || -t.teamId) === targetLeadId);
        if (!fromTeam || !toTeam) return prev;
        const memberObj = fromTeam.members.find(m => m.id === draggedMember.memberId);
        if (!memberObj) return prev;
        fromTeam.members = fromTeam.members.filter(m => m.id !== draggedMember.memberId);
        fromTeam.totalSubmissions = fromTeam.members.reduce((a, m) => a + m.submissionCount, 0);
        toTeam.members.push(memberObj);
        toTeam.totalSubmissions = toTeam.members.reduce((a, m) => a + m.submissionCount, 0);
        return updated;
      });

      setMoveMessage(`✅ ${draggedMember.memberName} moved successfully!`);
      setTimeout(() => setMoveMessage(''), 3000);
    } catch (err) {
      console.error('Error moving member:', err);
      setMoveMessage(`❌ Failed to move ${draggedMember.memberName}. Please try again.`);
      setTimeout(() => setMoveMessage(''), 4000);
    }
    setDraggedMember(null);
  };

  const handleDragEnd = () => {
    setDraggedMember(null);
    setDragOverLeadId(null);
  };

  const handleRemoveMember = async (member) => {
    if (!window.confirm(`Are you sure you want to remove ${member.name} from this team?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      try {
        await axios.delete(`${BASE_URL}/api/teams/remove-member/${member.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (delErr) {
        if (delErr.response && delErr.response.status === 404) {
          // Fallback to recruitment prefix if /api/teams is not found
          await axios.delete(`${BASE_URL}/api/recruitment/teams/remove-member/${member.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          throw delErr;
        }
      }

      // Optimistic UI update: member removed immediately without waiting for full refetch
      setReportData(prev => {
        return prev.map(team => ({
          ...team,
          members: (team.members || []).filter(m => m.id !== member.id)
        }));
      });

      setMoveMessage(`✅ ${member.name} removed successfully!`);
      setTimeout(() => setMoveMessage(''), 3000);

      // Refresh report
      fetchReport(startDate, endDate, regionFilter);
    } catch (err) {
      console.error('Error removing member:', err);
      setMoveMessage(`❌ Failed to remove ${member.name}. Please try again.`);
      setTimeout(() => setMoveMessage(''), 4000);
    }
  };

  // Process chart data for time-series visualization
  const getWeekNumber = (d) => {
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  };

  const getStartOfWeekString = (d) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(date.setDate(diff));
    return monday.toLocaleString('default', { month: 'short' }) + ' ' + monday.getDate();
  };

  const processedChartData = React.useMemo(() => {
    if (!trendData || trendData.length === 0) {
      return [{ name: (chartToggle === 'Yearly' ? new Date().getFullYear().toString() : (chartToggle === 'custom' ? 'Daily' : chartToggle)), ...reportData.reduce((acc, team) => ({ ...acc, [team.teamName]: team.totalSubmissions }), {}) }];
    }

    const recruiterToTeam = {};
    reportData.forEach(t => {
      if (t.members) {
        t.members.forEach(m => recruiterToTeam[m.name] = t.teamName);
      }
    });

    const grouped = {};

    trendData.forEach(item => {
      const d = new Date(item.date);
      let key;
      if (chartToggle === 'Daily' || chartToggle === 'custom') {
        key = item.date;
      } else if (chartToggle === 'Weekly') {
        key = 'W' + getWeekNumber(d) + ' (' + getStartOfWeekString(d) + ')';
      } else if (chartToggle === 'Monthly') {
        key = d.toLocaleString('default', { month: 'short' }) + ' ' + d.getFullYear();
      } else {
        key = d.getFullYear().toString();
      }

      if (!grouped[key]) {
        grouped[key] = { name: key, sortKey: d.getTime() };
      }
      const team = recruiterToTeam[item.recruiterName] || 'Unknown';
      if (!grouped[key][team]) grouped[key][team] = 0;
      grouped[key][team] += item.submissions;
    });

    return Object.values(grouped).sort((a, b) => a.sortKey - b.sortKey);
  }, [trendData, reportData, chartToggle]);

  return (
    <div className="job-report-container">
      <div className="job-report-header-section">
        <h2 className="report-header">Job Submission Report</h2>
        <div className="report-tabs">
          <button
            className={`tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </button>
          <button
            className={`tab-btn ${activeTab === 'existing' ? 'active' : ''}`}
            onClick={() => setActiveTab('existing')}
          >
            Team Pods
          </button>
          <button
            className={`tab-btn ${activeTab === 'clientReport' ? 'active' : ''}`}
            onClick={() => setActiveTab('clientReport')}
          >
            Client Report
          </button>
          {/* Hidden 📊 Report tab
          <button 
            className={`tab-btn ${activeTab === 'report' ? 'active' : ''}`}
            onClick={() => setActiveTab('report')}
          >
            📊 Report
          </button>
          */}
        </div>
        {/* Onsite / Offshore region toggle */}
        {activeTab !== 'clientReport' ? (
          <div className="region-toggle">
            <button
              className={`region-btn ${regionFilter === 'Onsite' ? 'active' : ''}`}
              onClick={() => {
                setRegionFilter('Onsite');
                fetchReport(startDate, endDate, 'Onsite');
              }}
            >
              Onsite
            </button>
            <button
              className={`region-btn ${regionFilter === 'Offshore' ? 'active' : ''}`}
              onClick={() => {
                setRegionFilter('Offshore');
                fetchReport(startDate, endDate, 'Offshore');
              }}
            >
              Offshore
            </button>
          </div>
        ) : (
          <div style={{ width: '160px', visibility: 'hidden' }} />
        )}
      </div>

      {/* Date filter section (visible for dashboard, existing pods, and report tabs) */}
      {activeTab !== 'clientReport' && (
        <div className="filters-section">
          <p className="filter-label">Date filters:</p>
          <div className="filter-inputs">
            <div className="input-group">
              <label>Quick Select</label>
              <select
                className="date-dropdown"
                value={dropdownSelection}
                onChange={handleDropdownChange}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="this_week">This Week</option>
                <option value="last_week">Last Week</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="this_quarter">This Quarter</option>
                <option value="last_quarter">Last Quarter</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
            <div className="input-group">
              <label>Start date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setDropdownSelection('custom');
                }}
                style={{ cursor: 'pointer' }}
              />
            </div>
            <div className="input-group">
              <label>End date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setDropdownSelection('custom');
                }}
                style={{ cursor: 'pointer' }}
              />
            </div>
            <button
              className="apply-btn"
              onClick={() => fetchReport()}
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : 'Apply'}
            </button>

            {/* Searchable Client Filter Dropdown */}
            <SearchableClientSelect
              clients={clientOptions}
              selectedClient={selectedClient}
              onSelect={(name) => {
                const newClient = name ? { name } : null;
                setSelectedClient(newClient);
                fetchReport(startDate, endDate, regionFilter, newClient);
              }}
            />
            {(dropdownSelection !== 'today' || regionFilter !== 'Onsite' || startDate !== today || endDate !== today || selectedClient !== null || activeFilter !== null) && (
              <button
                onClick={() => {
                  handleClearJobFilters();
                  // Reset all states
                  const todayVal = toYMD(new Date());
                  setStartDate(todayVal);
                  setEndDate(todayVal);
                  setDropdownSelection('today');
                  setRegionFilter('Onsite');
                  setSelectedClient(null);
                  setActiveFilter(null);
                  setChartToggle('Daily');
                  fetchReport(todayVal, todayVal, 'Onsite', null);
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #fca5a5',
                  background: '#fef2f2',
                  color: '#dc2626',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '13px',
                  alignSelf: 'flex-end',
                  height: '36px'
                }}
              >
                ✕ Clear All Filters
              </button>
            )}

            {/* Executive Report Dropdown Button */}
            <div ref={reportDropdownRef} style={{ position: 'relative', alignSelf: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setIsReportDropdownOpen(prev => !prev)}
                title="Download or view Executive Presentation Deck & Pipeline Report"
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #3b5bfd',
                  background: isReportDropdownOpen ? '#3b5bfd' : '#ffffff',
                  color: isReportDropdownOpen ? '#ffffff' : '#3b5bfd',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  height: '36px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => { if (!isReportDropdownOpen) e.currentTarget.style.background = '#eff6ff'; }}
                onMouseLeave={(e) => { if (!isReportDropdownOpen) e.currentTarget.style.background = '#ffffff'; }}
              >
                <Download size={14} /> Download Report <ChevronDown size={14} style={{ transform: isReportDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              </button>

              {isReportDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '6px',
                  background: '#ffffff',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15), 0 8px 10px -6px rgba(0,0,0,0.1)',
                  zIndex: 1000,
                  minWidth: '220px',
                  overflow: 'hidden'
                }}>
                  <div style={{ padding: '8px 12px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                    Select Report Period
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => handleGenerateReport('today')}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: 'none',
                      border: 'none',
                      textAlign: 'left',
                      fontSize: '13px',
                      color: '#1e293b',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f8fafc',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#1e293b'; }}
                  >
                    <span>⚡</span>
                    <div>
                      <div style={{ fontWeight: '600' }}>Today</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>Single day pipeline snapshot</div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleGenerateReport('weekly')}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: 'none',
                      border: 'none',
                      textAlign: 'left',
                      fontSize: '13px',
                      color: '#0f172a',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f8fafc',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#0f172a'; }}
                  >
                    <span>📊</span>
                    <div>
                      <div style={{ fontWeight: '700', color: '#3b5bfd' }}>Weekly (Executive Deck)</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>Full 14-slide PPTX-style review</div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleGenerateReport('monthly')}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: 'none',
                      border: 'none',
                      textAlign: 'left',
                      fontSize: '13px',
                      color: '#1e293b',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f8fafc',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#1e293b'; }}
                  >
                    <span>🗓️</span>
                    <div>
                      <div style={{ fontWeight: '600' }}>Monthly</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>Current month performance</div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleGenerateReport('custom')}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: 'none',
                      border: 'none',
                      textAlign: 'left',
                      fontSize: '13px',
                      color: '#1e293b',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f8fafc',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#1e293b'; }}
                  >
                    <span>⚙️</span>
                    <div>
                      <div style={{ fontWeight: '600' }}>Selected Date Range</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{startDate} — {endDate}</div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleGenerateReport('standard')}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: 'none',
                      border: 'none',
                      textAlign: 'left',
                      fontSize: '13px',
                      color: '#1e293b',
                      cursor: 'pointer',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#1e293b'; }}
                  >
                    <span>📑</span>
                    <div>
                      <div style={{ fontWeight: '600' }}>Standard Report</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>Complete team & recruiter analytics (PDF/Print)</div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
          {error && <p className="error-msg">{error}</p>}
        </div>
      )}

      {activeTab === 'clientReport' ? (
        <ClientCountReport />
      ) : activeTab === 'report' ? (
        <SubmissionReport
          reportData={reportData}
          trendData={trendData}
          activeFilter={activeFilter}
          onFilterChange={handleDashboardFilter}
          isLoading={isLoading}
          startDate={startDate}
          endDate={endDate}
        />
      ) : activeTab === 'dashboard' ? (
        <ManagerDashboard
          reportData={reportData}
          trendData={trendData}
          jobStats={jobStats}
          activeFilter={activeFilter}
          onFilterChange={handleDashboardFilter}
          isLoading={isLoading}
          startDate={startDate}
          endDate={endDate}
          region={regionFilter}
          client={selectedClient}
        />
      ) : (
        <>


          {/* Toast message for drag-and-drop feedback */}
          {moveMessage && (
            <div style={{
              position: 'fixed', top: '20px', right: '20px', zIndex: 9999,
              padding: '12px 20px', borderRadius: '8px',
              background: moveMessage.startsWith('✅') ? '#dcfce7' : '#fee2e2',
              color: moveMessage.startsWith('✅') ? '#166534' : '#991b1b',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)', fontWeight: '600', fontSize: '14px'
            }}>
              {moveMessage}
            </div>
          )}


          {/* Pod Performance Chart Section */}
          {reportData.length > 0 && (
            <div className="pod-performance-section" style={{ background: '#fff', borderRadius: '12px', padding: '24px', marginBottom: '32px', marginTop: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', color: '#1e293b' }}>Pod Performance</h3>
                <div className="filter-buttons">
                  {['daily', 'weekly', 'monthly', 'yearly'].map(f => (
                    <button
                      key={f}
                      className={`filter-btn ${activeFilter === f ? 'active' : ''}`}
                      onClick={() => {
                        setChartToggle(f.charAt(0).toUpperCase() + f.slice(1));
                        handleDashboardFilter(f);
                      }}
                    >
                      {f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'visible' }}>
                <div style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', padding: '12px 16px', color: '#1e293b', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '6px', background: '#e0e7ff', color: '#4f46e5' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg>
                  </span>
                  Submissions by Pod — {chartToggle === 'custom' ? 'Daily' : chartToggle}
                </div>
                <div style={{ padding: '24px', height: '350px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={processedChartData}
                      margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="name" axisLine={true} tickLine={true} tick={{ fill: '#64748b', fontSize: 13 }} />
                      <YAxis allowDecimals={false} axisLine={true} tickLine={true} tick={{ fill: '#64748b', fontSize: 13 }} />
                      <Tooltip
                        cursor={{ fill: '#f8fafc' }}
                        wrapperStyle={{ zIndex: 9999 }}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', zIndex: 9999, position: 'relative' }}
                      />
                      <Legend
                        iconType="square"
                        wrapperStyle={{ paddingTop: '20px' }}
                        content={() => (
                          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px', paddingTop: '16px' }}>
                            {reportData.map((team, index) => {
                              const podName = team.teamName;
                              const isActive = !highlightedPod || highlightedPod === podName;
                              const teamColor = extractSolidColor(team.colorTheme || cardColors[index % cardColors.length].bg);

                              return (
                                <div
                                  key={team.teamId || team.leadId || index}
                                  onClick={() => setHighlightedPod(highlightedPod === podName ? null : podName)}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    cursor: 'pointer',
                                    padding: '4px 10px',
                                    borderRadius: '20px',
                                    background: highlightedPod === podName ? teamColor + '18' : 'transparent',
                                    border: highlightedPod === podName ? `1.5px solid ${teamColor}` : '1.5px solid transparent',
                                    opacity: isActive ? 1 : 0.4,
                                    transition: 'all 0.2s ease',
                                    fontWeight: highlightedPod === podName ? 700 : 400,
                                    fontSize: '13px',
                                    color: '#475569',
                                    userSelect: 'none'
                                  }}
                                >
                                  <div style={{ width: 10, height: 10, background: teamColor, flexShrink: 0, borderRadius: '2px' }} />
                                  {podName}
                                </div>
                              );
                            })}
                            {highlightedPod && (
                              <div
                                onClick={() => setHighlightedPod(null)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: '4px',
                                  cursor: 'pointer', padding: '4px 10px', borderRadius: '20px',
                                  background: '#f1f5f9', fontSize: '12px', color: '#64748b',
                                  border: '1px solid #e2e8f0', transition: 'all 0.2s ease'
                                }}
                              >
                                ✕ Clear filter
                              </div>
                            )}
                          </div>
                        )}
                      />
                      {(highlightedPod ? reportData.filter(team => team.teamName === highlightedPod) : reportData).map((team, index) => {
                        const originalIndex = reportData.findIndex(t => t.teamName === team.teamName);
                        const teamColor = extractSolidColor(team.colorTheme || cardColors[originalIndex % cardColors.length].bg);
                        return (
                          <Bar
                            key={team.teamId || team.leadId || originalIndex}
                            dataKey={team.teamName}
                            fill={teamColor}
                            radius={[4, 4, 0, 0]}
                            barSize={40}
                          >
                            <LabelList
                              dataKey={team.teamName}
                              position="top"
                              style={{ fill: teamColor, fontSize: 12, fontWeight: 600 }}
                              formatter={(val) => (val > 0 ? val : '')}
                            />
                          </Bar>
                        );
                      })}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h3 className="section-title" style={{ margin: 0 }}>Team pods</h3>
            <button
              onClick={() => setIsCreatePodOpen(true)}
              style={{ padding: '6px 12px', background: '#3b5bfd', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              + Create New Pod
            </button>
          </div>

          <div className="team-grid">
            {reportData.length === 0 && !isLoading && (
              <p className="no-data">No data found for the selected date range. Click Apply to load data.</p>
            )}
            {reportData.map((team, index) => {
              const colorTheme = team.colorTheme
                ? { bg: getGradientFromSolid(team.colorTheme) }
                : cardColors[index % cardColors.length];
              const isDropTarget = dragOverLeadId === (team.leadId || -team.teamId);

              return (
                <div
                  key={index}
                  className="team-card"
                  onDragOver={(e) => handleDragOver(e, team.leadId || -team.teamId)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, team.leadId || -team.teamId)}
                  style={{
                    transform: isDropTarget ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.2s ease',
                    boxShadow: isDropTarget ? `0 0 0 2px ${colorTheme.bg}` : '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                >
                  <div className="team-card-header" style={{ background: colorTheme.bg }}>
                    <div className="team-header-left">
                      <div className="team-count-circle">
                        {team.members ? team.members.length : 0}
                      </div>
                      <div className="team-header-info">
                        <h4 className="team-name">{team.teamName}</h4>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '3px', flexWrap: 'wrap' }}>
                          <span className="team-region-badge">{regionFilter}</span>
                          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.92)', fontWeight: 600 }}>
                            {(team.totalSourced || 0)} Sourced • {(team.totalSubmissions || 0)} Subs
                          </span>
                          {team.teamType === 'BENCH' && (
                            <span style={{
                              display: 'inline-block',
                              marginLeft: '2px',
                              background: '#ffedd5',
                              color: '#c2410c',
                              padding: '1px 6px',
                              borderRadius: '10px',
                              fontSize: '10px',
                              fontWeight: 700,
                              border: '1px solid #fed7aa'
                            }}>BENCH</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="team-header-actions">
                      <span
                        onClick={() => {
                          setTargetPodData({ teamId: team.teamId || team.leadId, teamName: team.teamName, regionId: regionFilter === 'Onsite' ? '1' : '2', colorTheme: team.colorTheme, teamType: team.teamType });
                          setIsEditPodOpen(true);
                        }}
                        title="Edit Pod"
                      ><Edit2 size={14} /></span>
                      <span
                        onClick={() => {
                          setTargetPodData({ teamId: team.teamId || team.leadId, teamName: team.teamName });
                          setIsDeletePodOpen(true);
                        }}
                        title="Delete Pod"
                      ><Trash2 size={14} /></span>
                    </div>
                  </div>

                  <div className="team-body">
                    <ul className="member-list">
                      {team.members && team.members.map((member, mIndex) => (
                        <li
                          key={mIndex}
                          className="member-item"
                          style={{
                            borderLeft: `4px solid ${colorTheme.bg}`,
                            opacity: draggedMember && draggedMember.memberId === member.id ? 0.4 : 1,
                            transition: 'opacity 0.2s'
                          }}
                          draggable={true}
                          onDragStart={(e) => handleDragStart(e, member, team.leadId || -team.teamId)}
                          onDragEnd={handleDragEnd}
                          title="Drag to move to another team"
                        >
                          <div className="drag-handle" style={{ letterSpacing: '-2px' }}>
                            &#8942;&#8942;
                          </div>
                          <div className="member-info">
                            <span className="member-name">{member.name}</span>
                            <div style={{ display: 'flex', gap: '6px', fontSize: '11.5px', marginTop: '2px' }}>
                              <span style={{ color: '#4f46e5', fontWeight: 600 }}>{member.sourcedCount || 0} Sourced</span>
                              <span style={{ color: '#cbd5e1' }}>•</span>
                              <span className="member-subs" style={{ fontWeight: 600 }}>{member.submissionCount || 0} Subs</span>
                            </div>
                          </div>

                          <div className="member-actions">
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveMember(member);
                              }}
                              style={{ cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center' }}
                              title="Remove Member"
                            >
                              <Trash2 size={14} />
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>

                    <button
                      className="add-member-btn"
                      onClick={() => {
                        setTargetTeamIdForMember(team.teamId || team.leadId);
                        setIsAddMemberOpen(true);
                      }}
                    >
                      + Add Member
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pod Summary Table Section */}
          {reportData.length > 0 && (
            <div style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '32px' }}>
              {/* Dark header bar */}
              <div style={{
                background: '#0f172a',
                padding: '14px 24px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#f1f5f9', letterSpacing: '0.01em' }}>
                  Pod Summary
                </h3>
              </div>

              {/* Table */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                      <th style={{ padding: '11px 24px', textAlign: 'left', fontWeight: '600', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Pod</th>
                      <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: '600', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Type</th>
                      <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: '600', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Members</th>
                      <th style={{ padding: '11px 24px', textAlign: 'right', fontWeight: '600', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Total Submissions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((team, index) => {
                      const solidColor = extractSolidColor(team.colorTheme || cardColors[index % cardColors.length].bg);
                      const isOnsite = regionFilter === 'Onsite';
                      return (
                        <tr
                          key={index}
                          style={{
                            borderBottom: index < reportData.length - 1 ? '1px solid #f1f5f9' : 'none',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          {/* Pod name */}
                          <td style={{ padding: '13px 24px', fontWeight: '600', color: '#1e293b', whiteSpace: 'nowrap' }}>
                            {team.teamName}
                          </td>

                          {/* Type badge */}
                          <td style={{ padding: '13px 16px' }}>
                            {team.teamType === 'BENCH' ? (
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                                padding: '3px 10px',
                                borderRadius: '20px',
                                fontSize: '11px',
                                fontWeight: '700',
                                letterSpacing: '0.04em',
                                background: '#ffedd5',
                                color: '#c2410c',
                                border: '1px solid #fed7aa',
                              }}>
                                BENCH SALES
                              </span>
                            ) : (
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                                padding: '3px 10px',
                                borderRadius: '20px',
                                fontSize: '11px',
                                fontWeight: '700',
                                letterSpacing: '0.04em',
                                background: isOnsite ? '#eff6ff' : '#f0fdf4',
                                color: isOnsite ? '#1d4ed8' : '#15803d',
                                border: `1px solid ${isOnsite ? '#bfdbfe' : '#bbf7d0'}`,
                              }}>
                                {isOnsite ? (
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8" /><path d="M12 17v4" /></svg>
                                ) : (
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
                                )}
                                {isOnsite ? 'ONSITE' : 'OFFSHORE'}
                              </span>
                            )}
                          </td>

                          {/* Members chips */}
                          <td style={{ padding: '13px 16px' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                              {team.members && team.members.map((member, mIdx) => (
                                <span
                                  key={mIdx}
                                  style={{
                                    display: 'inline-block',
                                    padding: '3px 10px',
                                    borderRadius: '20px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    background: `${solidColor}18`,
                                    color: solidColor,
                                    border: `1px solid ${solidColor}33`,
                                  }}
                                >
                                  {member.name}
                                </span>
                              ))}
                              {(!team.members || team.members.length === 0) && (
                                <span style={{ color: '#94a3b8', fontSize: '12px', fontStyle: 'italic' }}>No members</span>
                              )}
                            </div>
                          </td>

                          {/* Total submissions */}
                          <td style={{ padding: '13px 24px', textAlign: 'right' }}>
                            <span style={{
                              fontWeight: '700',
                              fontSize: '16px',
                              color: solidColor,
                            }}>
                              {team.totalSubmissions}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </>
      )}

      {/* Modals */}
      <CreatePodModal
        isOpen={isCreatePodOpen}
        onClose={() => setIsCreatePodOpen(false)}
        onPodCreated={() => fetchReport(startDate, endDate, regionFilter)}
        currentRegion={regionFilter}
      />

      <AddMemberModal
        isOpen={isAddMemberOpen}
        onClose={() => setIsAddMemberOpen(false)}
        onMemberAdded={() => fetchReport(startDate, endDate, regionFilter)}
        targetTeamId={targetTeamIdForMember}
      />

      {isEditPodOpen && targetPodData && (
        <EditPodModal
          isOpen={isEditPodOpen}
          onClose={() => setIsEditPodOpen(false)}
          onPodUpdated={() => fetchReport(startDate, endDate, regionFilter)}
          podData={targetPodData}
        />
      )}

      {isDeletePodOpen && targetPodData && (
        <DeletePodModal
          isOpen={isDeletePodOpen}
          onClose={() => setIsDeletePodOpen(false)}
          onPodDeleted={() => fetchReport(startDate, endDate, regionFilter)}
          podData={targetPodData}
        />
      )}
    </div>
  );
};

export default JobReport;
